import { useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { auth } from "../lib/firebase";
import { sendMagicLink } from "../lib/emailLink";
import { PrimaryButton } from "./shared/PrimaryButton";
import { Footer, LEGAL_LINKS } from "./shared/Footer";

export function LoginScreen() {
  const { signInWithGoogle, authError } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);

  async function handleGoogle() {
    setBusy(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleMagicLink() {
    if (!auth) {
      setError("Firebase no está configurado.");
      return;
    }
    setLinkBusy(true);
    setError(null);
    setLinkSent(false);
    try {
      await sendMagicLink(auth, email);
      setLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo enviar el enlace.");
    } finally {
      setLinkBusy(false);
    }
  }

  const displayError = error ?? authError;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#ffffff" }}>
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-20">
        <div className="max-w-xl text-center">
          <h1
            className="text-5xl sm:text-6xl font-black tracking-tight"
            style={{ color: "#0f0f0f" }}
          >
            Formular
          </h1>
          <p
            className="mt-2 text-xs font-medium tracking-wider uppercase"
            style={{ color: "#a1a1aa" }}
          >
            by Formular
          </p>
          <div className="mt-10 flex flex-col items-center gap-3 w-full">
            <PrimaryButton onClick={handleGoogle} disabled={busy || linkBusy}>
              {busy ? "Signing in…" : "Sign in with Google"}
            </PrimaryButton>

            <div
              className="flex items-center gap-3 w-full max-w-sm my-2"
              style={{ color: "#a1a1aa" }}
            >
              <div className="flex-1 h-px" style={{ background: "rgba(0,0,0,0.10)" }} />
              <span className="text-xs">o</span>
              <div className="flex-1 h-px" style={{ background: "rgba(0,0,0,0.10)" }} />
            </div>

            {linkSent ? (
              <div
                className="text-sm max-w-sm rounded-2xl px-4 py-3 text-left"
                style={{
                  background: "rgba(0,0,0,0.04)",
                  border: "1px solid rgba(0,0,0,0.08)",
                  color: "#0f0f0f",
                }}
              >
                Te hemos enviado un enlace a <strong>{email}</strong>. Ábrelo
                desde el mismo dispositivo para completar el acceso. Revisa
                spam si no lo ves.
                <button
                  type="button"
                  onClick={() => {
                    setLinkSent(false);
                    setEmail("");
                  }}
                  className="block mt-2 underline text-xs"
                  style={{ color: "#71717a" }}
                >
                  Usar otro email
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2 w-full max-w-sm">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tu@empresa.com"
                  disabled={linkBusy || busy}
                  autoComplete="email"
                  className="w-full rounded-full px-4 py-2.5 text-sm focus:outline-none transition-all disabled:opacity-60"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.6)",
                    border: "1px solid rgba(0,0,0,0.14)",
                    color: "#0f0f0f",
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && email.includes("@")) {
                      handleMagicLink();
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={handleMagicLink}
                  disabled={linkBusy || busy || !email.includes("@")}
                  className="w-full rounded-full px-4 py-2.5 text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: "transparent",
                    color: "#0f0f0f",
                    border: "1px solid rgba(0,0,0,0.14)",
                  }}
                >
                  {linkBusy ? "Enviando…" : "Enviar enlace al email"}
                </button>
                <p className="text-xs" style={{ color: "#71717a" }}>
                  Acceso solo por invitación. Si tu organización ya te ha
                  invitado y tu email no es de Google, usa este método con el
                  email al que recibiste la invitación.
                </p>
              </div>
            )}

            <p className="text-xs max-w-sm mt-4" style={{ color: "#71717a" }}>
              By continuing you accept the{" "}
              <a
                href={LEGAL_LINKS.terms}
                target="_blank"
                rel="noreferrer noopener"
                className="underline"
                style={{ color: "#0f0f0f" }}
              >
                Terms
              </a>{" "}
              and{" "}
              <a
                href={LEGAL_LINKS.privacy}
                target="_blank"
                rel="noreferrer noopener"
                className="underline"
                style={{ color: "#0f0f0f" }}
              >
                Privacy Policy
              </a>
              .
              <br />
              <span style={{ color: "#a1a1aa" }}>
                Al continuar aceptas los{" "}
                <a
                  href={LEGAL_LINKS.terms}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline"
                  style={{ color: "#71717a" }}
                >
                  Términos
                </a>{" "}
                y la{" "}
                <a
                  href={LEGAL_LINKS.privacy}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="underline"
                  style={{ color: "#71717a" }}
                >
                  Política de privacidad
                </a>
                .
              </span>
            </p>
            {displayError && (
              <p className="text-sm" style={{ color: "#C61418" }}>
                {displayError}
              </p>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
